import type { Header } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import cn from 'classnames';

import { SortIndicator } from './sort-indicator';

import type { ColumnAlign, DataTableSorting } from '../lib/types';

type Props<TData> = {
  header: Header<TData, unknown>;
  sorting?: DataTableSorting;
  onSortChange?: (columnId: string) => void;
  sticky?: boolean;
};

const buttonAlignment: Record<ColumnAlign, string> = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
};

const thAlignment: Record<ColumnAlign, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

export function HeaderCell<TData>({
  header,
  sorting,
  onSortChange,
  sticky = false,
}: Props<TData>) {
  const meta = header.column.columnDef.meta;
  const align: ColumnAlign = meta?.align ?? 'left';
  const sortable = !!meta?.sortable && !!onSortChange;
  const active = sortable && sorting?.id === header.column.id;

  const headerContent = flexRender(
    header.column.columnDef.header,
    header.getContext()
  );

  const indicator = sortable && (
    <SortIndicator
      active={active}
      direction={active ? sorting?.direction : undefined}
    />
  );

  const ariaSort = sortable
    ? active
      ? sorting?.direction === 'asc'
        ? 'ascending'
        : 'descending'
      : 'none'
    : undefined;

  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      className={cn(
        `
          border-b border-separator bg-surface-neutral-1 px-2 py-3
          typography-text-xs typography-text-medium text-primitive-secondary
          lg:px-3
        `,
        thAlignment[align],
        sticky && 'sticky top-0 z-10'
      )}
    >
      {sortable ? (
        <button
          type="button"
          onClick={() => onSortChange?.(header.column.id)}
          className={cn(
            `
              m-0 inline-flex w-full cursor-pointer items-center gap-2 border-0
              bg-transparent p-0 text-inherit
            `,
            `
              transition-colors
              hover:text-primary
              focus:outline-none
              focus-visible:underline
            `,
            buttonAlignment[align]
          )}
        >
          {align === 'right' && indicator}
          {headerContent}
          {align !== 'right' && indicator}
        </button>
      ) : (
        headerContent
      )}
    </th>
  );
}
